import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase-config';

/**
 * What's On — the guest-homepage feed of a venue's published events and
 * promotions. One publish path, two front doors: the admin PWA's Event
 * Calendar / Promotions surfaces write `muuzboxEvents` / `venuePromotions`
 * docs, and every guest phone in the room reads them here.
 *
 * Matching is deliberately forgiving: docs are stamped with both the venue's
 * uid and its channel name, and historical docs vary in which they carry, so
 * we query by both and merge. All failures degrade to an empty feed — the
 * homepage must never break because marketing data is missing.
 */

const dedupeById = (rows) => {
  const seen = new Set();
  return rows.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
};

const collectBoth = async (collectionName, ch, uid) => {
  const queries = [];
  if (ch) queries.push(getDocs(query(collection(db, collectionName), where('channelName', '==', ch))));
  if (uid) queries.push(getDocs(query(collection(db, collectionName), where('venueUid', '==', uid))));
  const rows = [];
  const snaps = await Promise.allSettled(queries);
  snaps.forEach((s) => {
    if (s.status === 'fulfilled') s.value.forEach((d) => rows.push({ id: d.id, ...(d.data() || {}) }));
  });
  return dedupeById(rows);
};

export const fetchWhatsOn = async (clientObj) => {
  const ch = String(clientObj?.clientId || clientObj?.id || '').trim().toLowerCase();
  const uid = String(clientObj?.id || '').trim();
  if (!ch && !uid) return { events: [], promos: [] };

  const out = { events: [], promos: [] };
  const now = Date.now();

  try {
    const events = await collectBoth('muuzboxEvents', ch, uid);
    // Events stay visible through their own night (12h grace), undated
    // events (pool night placeholders etc.) always show.
    const cutoff = now - 12 * 3600e3;
    out.events = events
      .filter((e) => e.active !== false && (!e.startsAtMs || e.startsAtMs >= cutoff))
      .sort((a, b) => (a.startsAtMs || 9e15) - (b.startsAtMs || 9e15))
      .slice(0, 6);
  } catch (_) { /* feed stays empty */ }

  try {
    const promos = await collectBoth('venuePromotions', ch, uid);
    out.promos = promos
      .filter((p) => p.active !== false)
      .filter((p) => !p.startsAtMs || p.startsAtMs <= now)
      .filter((p) => !p.endsAtMs || p.endsAtMs >= now)
      .sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0))
      .slice(0, 4);
  } catch (_) { /* feed stays empty */ }

  return out;
};
